import React, { useState, useEffect } from "react";
import { fetchProjects, fetchFilters } from "../../fetchProjects";
import { Helmet } from "react-helmet";
import Header from "../../components/ui/Header";
import ProjectFilters from "./components/ProjectFilters";
import FeaturedProject from "./components/FeaturedProject";
import ProjectGrid from "./components/ProjectGrid";
import CallToAction from "./components/CallToAction";
import Footer from "../../components/ui/Footer";
import Icon from "../../components/AppIcon";

const ProjectsPortfolioShowcase = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [activeIndustry, setActiveIndustry] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allProjects, setAllProjects] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    types: [],
    industries: [],
  });

  useEffect(() => {
    const loadData = async () => {
      setLoading(!allProjects.length); // Only show loading if we don't have it yet
      const [projectsData, filtersData] = await Promise.all([
        fetchProjects(),
        fetchFilters(),
      ]);
      setAllProjects(projectsData);
      setFilterOptions(filtersData);
      setLoading(false);
    };

    loadData();
  }, []);

  // Featured project (most recent featured project by ID)
  const featuredProject =
    allProjects.length > 0
      ? allProjects
          .filter((p) => p.featured)
          .sort((a, b) => Number(b.id) - Number(a.id))[0] || allProjects[0]
      : null;

  // Filter projects based on active filters
  useEffect(() => {
    let filtered = allProjects;

    // 1. Deduplication: Exclude the featured project from the grid
    if (featuredProject) {
      filtered = filtered.filter(
        (project) => project.id !== featuredProject.id,
      );
    }

    // 2. Filter by Type
    if (activeFilter !== "all") {
      filtered = filtered.filter((project) => project.type === activeFilter);
    }

    // 3. Filter by Industry
    if (activeIndustry !== "all") {
      filtered = filtered.filter((project) =>
        Array.isArray(project.industry)
          ? project.industry.includes(activeIndustry)
          : project.industry === activeIndustry,
      );
    }

    // 4. Filter by Search Term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (project) =>
          project.title.toLowerCase().includes(term) ||
          project.description.toLowerCase().includes(term) ||
          project.type.toLowerCase().includes(term),
      );
    }

    setFilteredProjects(filtered);
  }, [activeFilter, activeIndustry, searchTerm, allProjects, featuredProject]);

  const handleIndustryChange = (industry) => {
    setActiveIndustry(industry);
  };

  return (
    <>
      <Helmet>
        <title>Projects Portfolio & Case Studies | Sudhan R</title>
        <meta
          name="description"
          content="Explore comprehensive research and technology case studies by Sudhan R. Featuring remote sensing, AI/ML, full-stack development, and STEM projects built in collaboration with ISRO and NASA paradigms."
        />
        <meta
          name="keywords"
          content="Sudhan R projects, ISRO fellowship, NASA STEM, remote sensing portfolio, full-stack case studies, vegetation health analysis, UAV mapping, NDVI, NDRE, QGIS, Pix4D, AI agriculture, React.js projects, Cloudflare Workers, STEM education outreach, software engineering portfolio"
        />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href="https://sudhan.live/projects" />

        <meta
          property="og:title"
          content="Projects Portfolio | Sudhan R – Tech & Research Case Studies"
        />
        <meta
          property="og:description"
          content="Discover high-impact projects by Sudhan R combining AI, remote sensing, and software engineering—including ISRO research frameworks and complex web innovations."
        />
        <meta
          property="og:image"
          content="https://sudhan.live/assets/images/Sudhan_2026_v3.png"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sudhan.live/projects" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Projects | Sudhan R Portfolio" />
        <meta name="twitter:description" content="Explore comprehensive research and technology case studies spanning Remote Sensing, AI/ML, and Full-Stack Development." />
        <meta name="twitter:image" content="https://sudhan.live/assets/images/Sudhan_2026_v3.png" />
        
        <meta name="author" content="Sudhan R" />
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "CollectionPage",
                "@id": "https://sudhan.live/projects#webpage",
                "url": "https://sudhan.live/projects",
                "name": "Projects Portfolio & Case Studies | Sudhan R",
                "description": "A comprehensive collection of research methodologies and technical implementations authored by Sudhan R.",
                "isPartOf": { "@id": "https://sudhan.live/#website" },
                "breadcrumb": { "@id": "https://sudhan.live/projects#breadcrumb" },
                "mainEntity": {
                  "@type": "ItemList",
                  "@id": "https://sudhan.live/projects#itemlist",
                  "name": "Featured Projects Portfolio",
                  "numberOfItems": 7,
                  "itemListElement": [
                    {
                      "@type": "ListItem",
                      "position": 1,
                      "item": {
                        "@type": "CreativeWork",
                        "@id": "https://sudhan.live/projects/vegetation-health-analysis#project",
                        "name": "Vegetation Health Analysis",
                        "description": "Used QGIS to analyze NDVI & NDRE from UAV and satellite data to identify stress in paddy, maize, and vegetable crops.",
                        "author": { "@id": "https://sudhan.live/#person" },
                        "keywords": ["UAV", "NDVI", "NDRE", "QGIS", "Pix4D", "Tableau", "Chlorophyll Index"]
                      }
                    },
                    {
                      "@type": "ListItem",
                      "position": 2,
                      "item": {
                        "@type": "SoftwareSourceCode",
                        "@id": "https://sudhan.live/projects/school-website-csi-vellore#project",
                        "name": "School Website – CSI Vellore Diocese MHSS",
                        "description": "Built a fully responsive school website with modules for admission, events, SEO optimization, and online forms.",
                        "author": { "@id": "https://sudhan.live/#person" },
                        "programmingLanguage": ["HTML", "Tailwind CSS", "JavaScript"],
                        "applicationCategory": "WebApplication"
                      }
                    },
                    {
                      "@type": "ListItem",
                      "position": 3,
                      "item": {
                        "@type": "SoftwareSourceCode",
                        "@id": "https://sudhan.live/projects/library-management-system#project",
                        "name": "Library Management System",
                        "description": "Created a full-stack library management system using React, HonoJS, Auth0, and TypeScript with high test coverage.",
                        "author": { "@id": "https://sudhan.live/#person" },
                        "programmingLanguage": ["React", "TypeScript", "HonoJS"],
                        "applicationCategory": "WebApplication"
                      }
                    },
                    {
                      "@type": "ListItem",
                      "position": 4,
                      "item": {
                        "@type": "CreativeWork",
                        "@id": "https://sudhan.live/projects/cloud-spotting-on-mars#project",
                        "name": "Cloud Spotting on Mars",
                        "description": "Martian cloud patterns through NASA STEM Gateway using orbiters and rover data for cloud classification.",
                        "author": { "@id": "https://sudhan.live/#person" },
                        "keywords": ["NASA", "Space Research", "Mars", "Cloud Imaging", "Data Analysis"]
                      }
                    },
                    {
                      "@type": "ListItem",
                      "position": 5,
                      "item": {
                        "@type": "SoftwareSourceCode",
                        "@id": "https://sudhan.live/projects/weather-app-javafx#project",
                        "name": "Weather Application (JavaFX)",
                        "description": "JavaFX-based desktop app for 5-day weather forecasting using OpenWeatherMap API.",
                        "author": { "@id": "https://sudhan.live/#person" },
                        "programmingLanguage": ["JavaFX", "Java"],
                        "applicationCategory": "DesktopApplication"
                      }
                    },
                    {
                      "@type": "ListItem",
                      "position": 6,
                      "item": {
                        "@type": "CreativeWork",
                        "@id": "https://sudhan.live/projects/sales-prediction#project",
                        "name": "Sales Prediction using Facebook Prophet",
                        "description": "Time-series prediction model for sales using Facebook Prophet. Achieved over 85% forecast accuracy.",
                        "author": { "@id": "https://sudhan.live/#person" },
                        "keywords": ["Data Science", "Time Series", "Prophet", "Sales Forecasting"]
                      }
                    },
                    {
                      "@type": "ListItem",
                      "position": 7,
                      "item": {
                        "@type": "CreativeWork",
                        "@id": "https://sudhan.live/projects/gravity-wave-detection#project",
                        "name": "Detection of Convection-Triggered Gravity Waves",
                        "description": "Detected atmospheric gravity waves using LIDAR and spectral decomposition techniques at ISRO NARL.",
                        "author": { "@id": "https://sudhan.live/#person" },
                        "keywords": ["LIDAR", "Atmospheric Science", "Spectral Analysis", "Gravity Waves", "ISRO"]
                      }
                    }
                  ]
                }
              },
              {
                "@type": "BreadcrumbList",
                "@id": "https://sudhan.live/projects#breadcrumb",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://sudhan.live/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Projects",
                    "item": "https://sudhan.live/projects"
                  }
                ]
              }
            ]
          }
          `}
        </script>
      </Helmet>

      <div className="min-h-screen bg-transparent text-text-primary">
        <Header />

        {/* Hero Section */}
        <section className="pt-20 lg:pt-28 pb-8 bg-background relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 opacity-50"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center space-x-2 bg-white/10 rounded-full px-4 py-2 mb-6">
                <Icon name="Rocket" size={16} className="text-white" />
                <span className="text-white text-sm font-medium">
                  Space-Tech • AI • Outreach
                </span>
              </div>

              <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-text-primary mb-6">
                Transforming Ideas into Impact
              </h1>

              <p className="text-text-secondary text-lg md:text-xl leading-relaxed max-w-3xl mx-auto mb-8">
                Dive into my journey through research, innovation, and
                education. From ISRO to NASA tutoring, I craft data-driven
                solutions that merge technology and curiosity for a better
                future.
              </p>

            </div>
          </div>
        </section>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-16">
          {/* Featured Project */}
          {featuredProject && (
            <div className="mb-12 border-b border-white/5 pb-4">
              <FeaturedProject project={featuredProject} />
            </div>
          )}

          {/* Project Search & Filters */}
          <div className="mb-8">
            <div className="relative mb-6">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Icon name="Search" size={20} className="text-text-muted" />
              </div>
              <input
                type="text"
                placeholder="Search projects by title, technology, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-xl shadow-2xl focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-smooth text-text-primary placeholder:text-text-muted"
              />
            </div>

            <ProjectFilters
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
              activeIndustry={activeIndustry}
              onIndustryChange={setActiveIndustry}
              projectCount={filteredProjects.length}
              types={filterOptions.types}
              industries={filterOptions.industries}
            />
          </div>

          {/* Projects Grid */}
          <section className="mb-16">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary">
                All Projects
              </h2>
              <div className="flex items-center space-x-2 text-text-muted">
                <Icon name="Grid3X3" size={16} />
                <span className="text-sm">Grid View</span>
              </div>
            </div>

            <ProjectGrid projects={filteredProjects} loading={loading} />
          </section>

          {/* Call to Action */}
          <CallToAction />
        </main>

        <Footer />
      </div>
    </>
  );
};

export default ProjectsPortfolioShowcase;
